import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, functions } from '../config/firebase';
import { setSessionMarker } from './sessionMarker';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bumped when the signed-in user's email becomes verified, so screens re-render
  const [, setVerifiedVersion] = useState(0);

  useEffect(() => {
    // Safety timeout - don't block rendering forever
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('Auth initialization timed out');
        setLoading(false);
      }
    }, 5000);

    let unsubscribeProfile = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      clearTimeout(timeout);
      setCurrentUser(user);
      // Keep the synchronous marker in step so the next cold load can decide
      // what to paint before Firebase has finished restoring the session.
      setSessionMarker(!!user);

      // Clean up previous profile listener
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (user) {
        // Set up real-time listener for user profile.
        // setLoading(false) is called inside the snapshot callback so that Firestore
        // queries in pages only fire after the auth token is fully established.
        unsubscribeProfile = onSnapshot(
          doc(db, 'users', user.uid),
          (docSnap) => {
            if (docSnap.exists()) {
              const profileData = docSnap.data();
              // Sync Firebase Auth emailVerified status to Firestore if out of sync
              if (user.emailVerified && !profileData.emailVerified) {
                updateDoc(doc(db, 'users', user.uid), { emailVerified: true });
              }
              setUserProfile(profileData);
            }
            setLoading(false);
          },
          (error) => {
            console.error('Error listening to user profile:', error);
            setLoading(false);
          }
        );
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(timeout);
      unsubscribeAuth();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);

  // leadRef: outreach CRM lead id from a demo follow-up trial link (?lead=)
  // referralCode: from a teacher's referral link (?ref=); checked server-side in onUserCreated
  // referralSource: affiliate link channel tag (&src=), stored with the referral
  // skipVerificationEmail: signing up from a school invite sent to this address (verified by schoolJoinOnSignup)
  const signup = async (email, password, displayName, leadRef = '', referralCode = '', referralSource = '', { skipVerificationEmail = false } = {}) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update profile with display name
    await updateProfile(userCredential.user, { displayName });

    // Create user document in Firestore
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email,
      displayName,
      createdAt: new Date().toISOString(),
      emailVerified: false,
      subscription: {
        tier: null,
        status: 'none',
        downloadsRemaining: 0,
        bespokeCredits: 0,
        bespokeCreditsHistory: [],
        periodStart: null,
        periodEnd: null,
      },
      isAdmin: false,
      ...(leadRef ? { leadRef } : {}),
      ...(referralCode ? { referralCode } : {}),
      ...(referralCode && referralSource ? { referralSource } : {}),
    });

    // Send branded verification email via Resend
    // Wrapped so signup never fails if the email send fails — user can resend from the verify page
    if (!skipVerificationEmail) {
      try {
        await httpsCallable(functions, 'sendVerificationEmail')();
      } catch (emailError) {
        console.warn('Initial verification email failed, user can resend from verify page:', emailError);
      }
    }

    return userCredential;
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    return signOut(auth);
  };

  const resetPassword = (email) => {
    return httpsCallable(functions, 'sendPasswordResetEmail')({ email });
  };

  const resendVerificationEmail = async () => {
    if (currentUser && !currentUser.emailVerified) {
      await httpsCallable(functions, 'sendVerificationEmail')();
    }
  };

  /**
   * Pick up an email that was verified in another tab or on another device.
   * Until the ID token is refreshed, security rules and Cloud Functions still
   * see email_verified: false (e.g. the dashboard can't load and school invites
   * can't be accepted). Returns whether the email is verified.
   */
  const refreshEmailVerified = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return false;
    try {
      if (!user.emailVerified) await user.reload();
      if (!auth.currentUser?.emailVerified) return false;
      // The account can say verified while the saved token still doesn't (e.g. a
      // tab opened by the verification link), so check the token itself
      const { claims } = await auth.currentUser.getIdTokenResult();
      if (!claims.email_verified) {
        await auth.currentUser.getIdToken(true);
        await updateDoc(doc(db, 'users', user.uid), { emailVerified: true }).catch(() => {});
        setVerifiedVersion((v) => v + 1);
      }
      return true;
    } catch (err) {
      console.warn('Could not check email verification:', err);
      return false;
    }
  }, []);

  // Check on load and whenever the tab comes back into view (the verification
  // link usually opens in a new tab). Cheap once verified: the token check is local.
  useEffect(() => {
    if (!currentUser) return undefined;
    refreshEmailVerified();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshEmailVerified(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [currentUser, refreshEmailVerified]);

  const refreshProfile = async () => {
    if (currentUser) {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setUserProfile(userDoc.data());
        }
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    }
  };

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    resendVerificationEmail,
    refreshEmailVerified,
    refreshProfile,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
